import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import API from '@/apiRequest';
import { useDispatch } from 'react-redux';
import { showToast } from '@/store/ducks/toasts';
import { useIntl } from 'react-intl';

export const useOrderMutations = orderId => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const intl = useIntl();
  const { pathname } = useLocation();
  const isInspection = pathname.includes('/inspections');
  const queryKeyName = isInspection ? 'inspection' : 'order';

  const onSuccess = data => {
    if (data && data.status) {
      queryClient.setQueryData([queryKeyName, orderId], prevData => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          status: data.status,
        };
      });
    }
    queryClient.invalidateQueries({ queryKey: [queryKeyName, orderId] });
  };

  const onError = error => {
    let message = intl.formatMessage({ id: 'MESSAGE.TRANSITION_FAILED' });
    if (error.response?.data?.error) {
      // Handle specific error codes if needed, or pass them to the caller
    }
    dispatch(
      showToast({
        type: 'error',
        title: intl.formatMessage({ id: 'ERROR' }),
        text: message,
        duration: 5000,
      })
    );
  };

  // Mutation for updating status (Approve, Reject, Send)
  const updateStatusMutation = useMutation({
    mutationFn: async ({ action, message, address }) => {
      const { data } = await API.post(`orders/${orderId}/status`, { action, message, address });
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for generic order updates (Marketplace ACCEPTED)
  const updateOrderMutation = useMutation({
    mutationFn: async payload => {
      const { data } = await API.put(`orders/${orderId}`, payload);
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for syncing envelope
  const syncEnvelopeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await API.put(`orders/${orderId}/sync`);
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for resending envelope
  const resendEnvelopeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await API.put(`orders/${orderId}`, { type: 'docusign', action: 'resend' });
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for confirming croqui sent
  const confirmCroquiSentMutation = useMutation({
    mutationFn: async ({ filename }) => {
      const { data } = await API.put(`/orders/${orderId}/croqui-sent`, { filename });
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for adding recommendation code
  const addRecommendationMutation = useMutation({
    mutationFn: async ({ code }) => {
      const { data } = await API.post(`orders/${orderId}/recommendations`, { code });
      return data;
    },
    onSuccess,
    onError,
  });

  // Mutation for adding performing inspector
  const addInspectorMutation = useMutation({
    mutationFn: async payload => {
      const { data } = await API.post(`/orders/${orderId}/inspectors`, payload);
      return data;
    },
    onSuccess,
    onError,
  });

  return {
    updateStatus: updateStatusMutation,
    updateOrder: updateOrderMutation,
    syncEnvelope: syncEnvelopeMutation,
    resendEnvelope: resendEnvelopeMutation,
    confirmCroquiSent: confirmCroquiSentMutation,
    addRecommendation: addRecommendationMutation,
    addInspector: addInspectorMutation,
    updateOrderRepairBudget: useMutation({
      mutationFn: async budgetData => {
        const { data } = await API.post(`orders/${orderId}/repair-budget`, budgetData);
        return data;
      },
      onSuccess,
      onError,
    }),
  };
};
